import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { scannerApi, apiMessage, extractData } from 'shared/api/scanner'
import { useAuth } from 'context/AuthContext'
import { PERMISSIONS, hasPermission } from 'shared/util/permissions'
import { PermissionEmpty, Skeleton } from 'shared/components/ui/primitives'
import Button from 'shared/components/ui/button'
import PropTypes from 'prop-types'

function Donut({ inside, outside }) {
  const total = Math.max(1, (inside || 0) + (outside || 0))
  const insidePct = ((inside || 0) / total) * 100
  return (
    <div className="relative mx-auto h-40 w-40">
      <div
        className="h-full w-full rounded-full"
        style={{
          background: `conic-gradient(#09B945 0 ${insidePct}%, #F7A201 ${insidePct}% 100%)`
        }}
      />
      <div className="absolute inset-4 flex flex-col items-center justify-center rounded-full bg-surface">
        <span className="text-3xl font-bold">{Math.round(insidePct)}%</span>
        <span className="text-xxs text-muted">inside</span>
      </div>
    </div>
  )
}

Donut.propTypes = {
  inside: PropTypes.number,
  outside: PropTypes.number
}

function Reports() {
  const { eventId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [updatedAt, setUpdatedAt] = useState(null)
  const [error, setError] = useState('')

  const load = async () => {
    try {
      setError('')
      const res = await scannerApi.reports(eventId)
      setInfo(extractData(res))
      setUpdatedAt(new Date())
    } catch (err) {
      setError(apiMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!hasPermission(user, eventId, PERMISSIONS.GET_REPORT)) {
      setLoading(false)
      return undefined
    }
    load()
    const id = setInterval(load, 15000)
    return () => clearInterval(id)
  }, [eventId, user])

  if (!hasPermission(user, eventId, PERMISSIONS.GET_REPORT)) return <PermissionEmpty />

  if (loading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-40" />
      </div>
    )
  }

  const inside = info?.nPeopleInsideEvent ?? 0
  const outside = info?.nPeopleOutsideEvent ?? 0

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="mb-1 text-4xl font-bold">Attendance</h2>
          <p className="mb-0 flex items-center gap-2 text-xs text-muted">
            <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
            Live · {updatedAt ? updatedAt.toLocaleTimeString() : '—'}
          </p>
        </div>
        {hasPermission(user, eventId, PERMISSIONS.EVENT_REPORT) && (
          <Button isSmall variant="ghost" onClick={() => navigate(`/events/${eventId}/sales`)}>
            Sales
          </Button>
        )}
      </div>

      {error && <p className="text-danger">{error}</p>}

      <div className="mb-4 grid grid-cols-2 gap-3">
        <Stat label="Total tickets" value={info?.nTotal} tone="text-secondary-500" />
        <Stat label="Inside now" value={info?.nInsideEvent} tone="text-success" />
        <Stat label="Outside" value={info?.nOutsideEvent} tone="text-primary" />
        <Stat label="People inside" value={inside} tone="text-success" />
      </div>
      <div className="card-surface p-5">
        <p className="mb-4 text-center text-sm text-muted">
          People inside {inside} / outside {outside}
        </p>
        <Donut inside={inside} outside={outside} />
      </div>
    </div>
  )
}

function Stat({ label, value, tone }) {
  return (
    <div className="card-surface p-4">
      <p className={`mb-1 text-5xl font-bold ${tone}`}>{value ?? 0}</p>
      <p className="mb-0 text-xs text-muted">{label}</p>
    </div>
  )
}

Stat.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  tone: PropTypes.string
}

export default Reports
